import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';

/*
 * Vista struttura (super admin, 14/10/2026): il super admin apre un prodotto (corso, squadra, abbonamento, lezione,
 * impianto) con la pagina della struttura (`/struttura/...`), esattamente come lo vede lei, in sola lettura.
 *
 * - Si entra dalle liste dei prodotti del super admin (superadmin/booking/...), cliccando una riga: `apriComeLaStruttura`.
 * - Finche' il super admin resta sulle pagine `/struttura/...` ogni chiamata al backend porta il header
 *   `x-sportid-vista-struttura`: il backend risponde come alla struttura e accetta solo letture (GET).
 * - Qui le scritture si fermano anche prima di partire; i tasti che modificano non si vedono e i campi delle
 *   schede sono bloccati (`ContenutoVistaStruttura`). Ricerca e filtri delle liste restano usabili.
 * - Uscendo dalle pagine `/struttura/...` la vista si chiude da sola.
 */

const CHIAVE = 'sidVistaStruttura';
export const HEADER_VISTA_STRUTTURA = 'x-sportid-vista-struttura';
export const MESSAGGIO_SOLA_LETTURA = 'Stai guardando la struttura in sola lettura: da qui non si modifica nulla.';

const apiBase = () => String(process.env.REACT_APP_API_BASE_PATH || '').replace(/\/+$/, '');

export const percorsoDellaVista = (pathname) => typeof pathname === 'string' && pathname.startsWith('/struttura/');

// Nella vista restano aperte le schede dei prodotti e le pagine collegate (clienti, prenotazioni). Le liste
// della struttura e le altre pagine (dashboard…) riportano invece alla lista del super admin da cui si e'
// partiti: ci si arriva da «← Elenco…», da «Annulla», dal percorso in alto.
const LISTE_DELLA_STRUTTURA = ['/struttura/corsi', '/struttura/corsi/squadre', '/struttura/booking/subscriptions',
    '/struttura/booking/lessons', '/struttura/booking/facilities'];
const PAGINE_DELLA_VISTA = ['/struttura/corsi/', '/struttura/booking/', '/struttura/anagrafiche/'];
export const fuoriDallaVista = (pathname) => {
    const percorso = String(pathname || '').replace(/\/+$/, '');
    if (LISTE_DELLA_STRUTTURA.includes(percorso)) return true;
    return !PAGINE_DELLA_VISTA.some((inizio) => percorso.startsWith(inizio));
};

/** La struttura in vista ({ clubId, nome }), anche se il super admin e' su un'altra pagina. */
export const strutturaInVista = () => {
    try {
        const v = JSON.parse(sessionStorage.getItem(CHIAVE) || 'null');
        return v && v.clubId ? v : null;
    } catch (e) {
        return null;
    }
};

/** La vista vale solo per il super admin e solo sulle pagine della struttura. */
export const vistaStrutturaAttiva = () => {
    if (sessionStorage.getItem('userType') !== 'superadmin') return null;
    if (!percorsoDellaVista(window.location.pathname)) return null;
    return strutturaInVista();
};

export const entraInVistaStruttura = ({ clubId, nome, ritorno }) => {
    sessionStorage.setItem(CHIAVE, JSON.stringify({ clubId, nome: nome || '', ritorno: ritorno || '' }));
};

/**
 * Apre la pagina della struttura (`percorso`, es. `/struttura/corsi/123`) per il prodotto di `clubId`, in sola
 * lettura; «Torna all'elenco» riporta alla pagina da cui si parte.
 */
export const apriComeLaStruttura = (navigate, { clubId, nome, percorso }) => {
    if (!clubId) return;
    entraInVistaStruttura({ clubId, nome, ritorno: `${window.location.pathname}${window.location.search}` });
    navigate(percorso);
};

export const esciDaVistaStruttura = () => sessionStorage.removeItem(CHIAVE);

// Ogni chiamata al backend dalla vista porta il header; le scritture non partono.
axios.interceptors.request.use((config) => {
    const vista = vistaStrutturaAttiva();
    const base = apiBase();
    if (!vista || !base || !String(config.url || '').startsWith(base)) return config;
    if ((config.method || 'get').toLowerCase() !== 'get') {
        const errore = new Error(MESSAGGIO_SOLA_LETTURA);
        errore.response = { status: 403, data: { detail: MESSAGGIO_SOLA_LETTURA } };
        return Promise.reject(errore);
    }
    config.headers = config.headers || {};
    config.headers[HEADER_VISTA_STRUTTURA] = vista.clubId;
    return config;
});

// Campi che restano usabili: ricerca, filtri e paginazione delle liste.
const ZONE_LIBERE = '.sid-list-header, .footTab, .sid-vista-barra, .nav-tabs, .sid-tabs-compatto';
const CAMPI = 'input, textarea, select';

const bloccaCampi = (radice) => {
    radice.querySelectorAll(CAMPI).forEach((campo) => {
        if (campo.closest(ZONE_LIBERE) || campo.disabled) return;
        if (campo.type === 'hidden') return;
        campo.disabled = true;
        campo.setAttribute('data-sid-vista', '1');
    });
};

/**
 * Il contenuto della pagina nella vista struttura: barra in alto (quale struttura, sola lettura, ritorno
 * all'elenco) e campi bloccati. Fuori dalla vista mostra solo il contenuto.
 */
export const ContenutoVistaStruttura = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const radice = useRef(null);
    const superAdmin = sessionStorage.getItem('userType') === 'superadmin';
    const nellaVista = superAdmin && percorsoDellaVista(location.pathname);
    const vista = nellaVista ? strutturaInVista() : null;

    // Lasciando le pagine della struttura la vista si chiude; tornando a una lista della struttura (o a una
    // pagina che non e' di un prodotto) si torna invece alla lista del super admin, con ricerca e filtri.
    useEffect(() => {
        if (!superAdmin) return;
        if (!percorsoDellaVista(location.pathname)) { esciDaVistaStruttura(); return; }
        const inVista = strutturaInVista();
        if (inVista && fuoriDallaVista(location.pathname)) {
            esciDaVistaStruttura();
            navigate(inVista.ritorno || '/superadmin/dashboard', { replace: true });
        }
    }, [superAdmin, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    // I campi compaiono man mano che arrivano i dati: si bloccano appena entrano nella pagina.
    useEffect(() => {
        if (!vista || !radice.current) return undefined;
        const nodo = radice.current;
        bloccaCampi(nodo);
        const osservatore = new MutationObserver(() => bloccaCampi(nodo));
        osservatore.observe(nodo, { childList: true, subtree: true });
        return () => osservatore.disconnect();
    }, [vista?.clubId, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!vista) return children;

    const tornaAllElenco = () => {
        esciDaVistaStruttura();
        navigate(vista.ritorno || '/superadmin/dashboard');
    };

    return (
        <div className="sid-vista-struttura" ref={radice}>
            <div className="sid-vista-barra">
                <span className="sid-vista-barra__testo">
                    Stai vedendo come la vede <strong>{vista.nome || 'la struttura'}</strong>, in sola lettura.
                </span>
                <Button className="btn btn-light sid-btn-compatto" onClick={tornaAllElenco}>Torna all'elenco</Button>
            </div>
            {children}
        </div>
    );
};
